import { IntegrationType, ConnectedAccountSyncEventSchema, SyncStatus } from "@nova/schemas"
import { useAppSelector } from "integrations/redux/hooks"
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    ReactNode,
} from "react"
import useSWRMutation from "swr/mutation"
import { selectActiveWorkspaceId } from "../workspace/WorkspaceManagementSlice"
import {
    invalidateIntegrationAuthorizations,
    useIntegrationAuthorizations,
} from "../integrations/useIntegrations"
import Connections, { AccountsByIntegrationType, SyncInfo } from "./module"
import { socket } from "integrations/redux/store"
export type { AccountsByIntegrationType, SyncInfo }

const usePilotTrackingConnections = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const { isLoading: isLoadingDjiIntegrations, data: djiIntegrations } =
        useIntegrationAuthorizations({
            workspaceId: orgId,
            integration: IntegrationType.dji,
            authType: "dji",
        })
    const { isLoading: isLoadingAutelIntegrations, data: autelIntegrations } =
        useIntegrationAuthorizations({
            workspaceId: orgId,
            integration: IntegrationType.autel,
            authType: "autel",
        })
    const { isLoading: isLoadingSkydioIntegrations, data: skydioIntegrations } =
        useIntegrationAuthorizations({
            workspaceId: orgId,
            integration: IntegrationType.skydio,
            authType: "token",
        })

    const accountsByIntegrationType = useMemo(
        () =>
            Connections.buildAccountsByIntegrationType(
                djiIntegrations,
                autelIntegrations,
                skydioIntegrations
            ),
        [djiIntegrations, autelIntegrations, skydioIntegrations]
    )

    const [syncStatuses, setSyncStatuses] = useState<Record<string, SyncInfo | undefined>>({})
    const [isSyncStatusesLoading, setIsSyncStatusesLoading] = useState(!!orgId)

    useEffect(() => {
        if (!orgId) return
        setIsSyncStatusesLoading(true)
        Connections.fetchSyncStatuses(["pilot_tracking_syncs", orgId])
            .then(setSyncStatuses)
            .finally(() => setIsSyncStatusesLoading(false))
    }, [orgId])

    const refetchSyncStatuses = useCallback(async () => {
        if (!orgId) return
        const data = await Connections.fetchSyncStatuses(["pilot_tracking_syncs", orgId])
        setSyncStatuses(data)
    }, [orgId])

    const allIntegrationIdsKey = useMemo(
        () =>
            Object.values(accountsByIntegrationType)
                .flat()
                .map((i) => i.id)
                .sort()
                .join(","),
        [accountsByIntegrationType]
    )

    useEffect(() => {
        const ids = allIntegrationIdsKey.split(",").filter((id) => !!id)

        const handleSyncUpdated = (payload: string) => {
            const { data } = ConnectedAccountSyncEventSchema.safeParse(JSON.parse(payload))
            if (!data) return
            if (!ids.includes(data.integrationId)) return
            setSyncStatuses((prev) => ({
                ...prev,
                [data.integrationId]:
                    data.status === SyncStatus.Syncing
                        ? {
                              status: "syncing" as const,
                              triggeredAt: prev[data.integrationId]?.triggeredAt ?? new Date(),
                              filesTotal: data.totalCount,
                              filesSynced: data.successCount,
                          }
                        : {
                              status: data.status as "synced" | "error",
                              triggeredAt: prev[data.integrationId]?.triggeredAt ?? new Date(),
                          },
            }))
        }

        const subscribeToSyncEvents = () => {
            ids.forEach((id) => socket?.emit("subscribeToSyncEvents", id))
            socket?.on("syncUpdated", handleSyncUpdated)
        }

        if (socket?.connected) {
            subscribeToSyncEvents()
        } else {
            socket?.on("connect", subscribeToSyncEvents)
        }

        return () => {
            ids.forEach((id) => socket?.emit("unsubscribeFromSyncEvents", id))
            socket?.off("syncUpdated", handleSyncUpdated)
            socket?.off("connect", subscribeToSyncEvents)
        }
    }, [allIntegrationIdsKey])

    const createNewDjiConnection = async (email: string, password: string) =>
        await Connections.createNewDjiConnection(
            orgId,
            email,
            password,
            invalidateIntegrationAuthorizations
        )

    const updateDjiConnection = async (integrationId: string, password: string) =>
        await Connections.updateDjiConnection(
            orgId,
            integrationId,
            { password },
            invalidateIntegrationAuthorizations
        )

    const deleteDjiConnection = async (integrationId: string) =>
        await Connections.deleteDjiConnection(
            orgId,
            integrationId,
            invalidateIntegrationAuthorizations
        )

    const createNewAutelConnection = async (email: string, password: string) =>
        await Connections.createNewAutelConnection(
            orgId,
            email,
            password,
            invalidateIntegrationAuthorizations
        )

    const updateAutelConnection = async (integrationId: string, password: string) =>
        await Connections.updateAutelConnection(
            orgId,
            integrationId,
            { password },
            invalidateIntegrationAuthorizations
        )

    const deleteAutelConnection = async (integrationId: string) =>
        await Connections.deleteAutelConnection(
            orgId,
            integrationId,
            invalidateIntegrationAuthorizations
        )

    const createNewSkydioConnection = async (apiToken: string) => {
        const integration = await Connections.createNewSkydioConnection(
            orgId,
            apiToken,
            invalidateIntegrationAuthorizations
        )
        if (integration) {
            void triggerSkydioSync(integration.id)
        }
    }

    const updateSkydioConnection = async (integrationId: string, apiToken: string) => {
        const integration = await Connections.updateSkydioConnection(
            orgId,
            integrationId,
            { apiToken },
            invalidateIntegrationAuthorizations
        )
        if (integration) {
            void triggerSkydioSync(integration.id)
        }
    }

    const deleteSkydioConnection = async (integrationId: string) =>
        await Connections.deleteSkydioConnection(
            orgId,
            integrationId,
            invalidateIntegrationAuthorizations
        )

    const { trigger: triggerDjiSyncMutation } = useSWRMutation(
        orgId ? ["dji_sync", orgId] : null,
        Connections.triggerDjiSync
    )

    const { trigger: triggerAutelSyncMutation } = useSWRMutation(
        orgId ? ["autel_sync", orgId] : null,
        Connections.triggerAutelSync
    )

    const { trigger: triggerSkydioSyncMutation } = useSWRMutation(
        orgId ? ["skydio_sync", orgId] : null,
        Connections.triggerSkydioSync
    )

    const triggerSync = async (
        integrationId: string,
        trigger: (arg: {
            integrationId: string
            invalidate: () => Promise<unknown>
        }) => Promise<unknown>
    ) => {
        const triggeredAt = new Date()
        try {
            await trigger({ integrationId, invalidate: refetchSyncStatuses })
        } catch (err) {
            setSyncStatuses((prev) => ({
                ...prev,
                [integrationId]: { status: "error" as const, triggeredAt },
            }))
            throw err
        }
    }

    const triggerDjiSync = (integrationId: string) =>
        triggerSync(integrationId, triggerDjiSyncMutation)

    const triggerAutelSync = (integrationId: string) =>
        triggerSync(integrationId, triggerAutelSyncMutation)

    const triggerSkydioSync = (integrationId: string) =>
        triggerSync(integrationId, triggerSkydioSyncMutation)

    const isLoading =
        isSyncStatusesLoading ||
        isLoadingDjiIntegrations ||
        isLoadingAutelIntegrations ||
        isLoadingSkydioIntegrations

    return {
        accountsByIntegrationType,
        hasConnectedAccount: !!Object.keys(accountsByIntegrationType).length,
        syncStatuses,
        isLoading,

        // DJI
        createNewDjiConnection,
        updateDjiConnection,
        deleteDjiConnection,
        triggerDjiSync,

        // Autel
        createNewAutelConnection,
        updateAutelConnection,
        deleteAutelConnection,
        triggerAutelSync,

        // Skydio
        createNewSkydioConnection,
        updateSkydioConnection,
        deleteSkydioConnection,
        triggerSkydioSync,
    }
}

type PilotTrackingConnectionsContextValue = ReturnType<typeof usePilotTrackingConnections>

const PilotTrackingConnectionsContext = createContext<
    PilotTrackingConnectionsContextValue | undefined
>(undefined)

export const PilotTrackingConnectionsProvider = ({ children }: { children: ReactNode }) => {
    const value = usePilotTrackingConnections()
    return (
        <PilotTrackingConnectionsContext.Provider value={value}>
            {children}
        </PilotTrackingConnectionsContext.Provider>
    )
}

export const usePilotTrackingConnectionsContext = (): PilotTrackingConnectionsContextValue => {
    const context = useContext(PilotTrackingConnectionsContext)
    if (!context) {
        throw new Error(
            "usePilotTrackingConnectionsContext must be used within a PilotTrackingConnectionsProvider"
        )
    }
    return context
}
